import { isSameDay } from 'date-fns'
import React from 'react'
import type { SupportedLocale } from 'zaptic-types'
import type { KnownUnit } from '../../../shared/domain/units/types.no-deps'
import type { HistoricalReport } from '../../../shared/domain/viewableReports/types.no-deps'
import { Translations } from '../../../shared/i18n/Translations'
import { displayDate } from '../../../shared/lib/date.no-deps'
import type { CommonDeps } from '../base/CommonDeps'
import type { RenderCachedImage } from '../image/WithCachedImage'
import { UnitImage } from '../unit/UnitImage'

export function getReportHistoryMetaData(
    commonDeps: CommonDeps,
    renderCachedImage: RenderCachedImage,
    report: HistoricalReport,
    usePlaceholderForEmptySubject = false
) {
    const reportName = (
        <MetadataItem labelKey="appcore:ReportHistory.reportName">{report.reportViewData.title}</MetadataItem>
    )

    const reportSubject = (
        <ReportSubject
            commonDeps={commonDeps}
            renderCachedImage={renderCachedImage}
            subject={report.reportSubjectUnit}
            usePlaceholderForEmptySubject={usePlaceholderForEmptySubject}
        />
    )

    const description = !!report.originInfo ? (
        <MetadataItem labelKey="appcore:common.description">
            {!!report.originInfo.description && <>&ldquo;{report.originInfo.description}&rdquo;</>}
        </MetadataItem>
    ) : null

    const createdBy = !!report.originInfo?.isAction ? (
        <MetadataItem labelKey="appcore:ReportHistory.actionCreatedBy">
            <ActionCreator originInfo={report.originInfo} />
        </MetadataItem>
    ) : null

    const dateAndTime = (
        <MetadataItem labelKey="appcore:common.dateAndTime">
            <DateAndTimeSummary
                startDate={report.report.startDate}
                endDate={report.report.endDate}
                locale={commonDeps.locale}
            />
        </MetadataItem>
    )

    return { reportName, reportSubject, description, createdBy, dateAndTime }
}

function MetadataItem(props: { labelKey: string; children: React.ReactNode }) {
    return (
        <div className="AppCore__report__HistoryMetaData__Item">
            <span className="AppCore__report__HistoryMetaData__Item-label">{Translations.t(props.labelKey)}</span>
            <span className="AppCore__report__HistoryMetaData__Item-value">{props.children}</span>
        </div>
    )
}

function ReportSubject(props: {
    commonDeps: CommonDeps
    renderCachedImage: RenderCachedImage
    subject: KnownUnit | null
    usePlaceholderForEmptySubject: boolean
}) {
    let content: JSX.Element | string

    if (props.subject) {
        content = (
            <div className="AppCore__report__HistoryMetaData__Subject">
                <UnitImage deps={props.commonDeps} renderCachedImage={props.renderCachedImage} unit={props.subject} />
                <zaptic-ellipsis>{props.subject.name}</zaptic-ellipsis>
            </div>
        )
    } else if (props.usePlaceholderForEmptySubject) {
        content = '-'
    }

    return content ? <MetadataItem labelKey="shared:reportSubject.title">{content}</MetadataItem> : null
}

function ActionCreator({ originInfo }: { originInfo: HistoricalReport['originInfo'] }) {
    if (originInfo.createdManually) {
        return (
            <div className="AppCore__report__HistoryMetaData__TriggerCreator">
                <zaptic-profile-circle
                    name={originInfo.createdBy.name}
                    color={originInfo.createdBy.profileCircleColor}
                    profile-image-url={originInfo.createdBy.profileImageUrl}
                />{' '}
                <span>{originInfo.createdBy.name}</span>
            </div>
        )
    }

    return (
        <div className="AppCore__report__HistoryMetaData__TriggerCreator">
            <zaptic-icon class="AppCore__report__HistoryMetaData__TriggerIcon" icon-name="flow-triggers" />
            <span>{Translations.t('appcore:ReportHistory.automatedByTrigger')}</span>
        </div>
    )
}

function DateAndTimeSummary({
    startDate,
    endDate,
    locale,
}: {
    startDate: Date
    endDate: Date
    locale: SupportedLocale
}) {
    const areSameDate = isSameDay(startDate, endDate)
    const formattedStartDate = displayDate(locale, startDate, { dateStyle: 'short' })
    const formattedStartTime = displayDate(locale, startDate, { timeStyle: 'short' })
    const formattedEndTime = displayDate(locale, endDate, { timeStyle: 'short' })
    if (areSameDate) {
        return (
            <span>
                {formattedStartDate}, {formattedStartTime} - {formattedEndTime}
            </span>
        )
    } else {
        const formattedEndDate = displayDate(locale, endDate, { dateStyle: 'short' })
        return (
            <div style={{ display: 'flex', flexDirection: 'column' }}>
                <span>
                    {formattedStartDate}, {formattedStartTime}
                </span>
                <span>
                    {formattedEndDate}, {formattedEndTime}
                </span>
            </div>
        )
    }
}
